(() => {
  'use strict';

  if (window.DarPrirodeProductPage?.loaded) return;

  const initializedRoots = new WeakSet();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const ROOT_SELECTOR = '[data-dp-product-root]';

  const parseJSON = (element, fallback = null) => {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('[Dar Prirode] Neispravan JSON podatak.', error);
      return fallback;
    }
  };

  const getShopifyRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'BAM';
    const locale = document.documentElement.lang || 'bs-BA';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2).replace('.', ',')} KM`;
    }
  };

  const dispatchCartEvents = (detail) => {
    document.dispatchEvent(new CustomEvent('dp:cart:updated', { detail }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
    document.dispatchEvent(new CustomEvent('cart:update', { detail }));
  };

  const getRenderableSectionIds = () => {
    const ids = new Set();

    document.querySelectorAll('cart-items-component[data-section-id], [data-cart-section-id]').forEach((component) => {
      const id = component.dataset.sectionId || component.dataset.cartSectionId;
      if (id) ids.add(id);
    });

    document.querySelectorAll('[id^="shopify-section-cart-drawer"]').forEach((section) => {
      const id = section.id.replace(/^shopify-section-/, '');
      if (id) ids.add(id);
    });

    if (document.getElementById('shopify-section-cart-drawer-section')) {
      ids.add('cart-drawer-section');
    }

    return Array.from(ids).slice(0, 5);
  };

  const getCartDrawerElements = (scope = document) => {
    const drawer = scope.querySelector?.(
      'theme-drawer#cart-drawer, #cart-drawer, cart-drawer, cart-drawer-component'
    ) || null;

    const themeDrawer = drawer?.matches?.('theme-drawer')
      ? drawer
      : drawer?.closest?.('theme-drawer') || scope.querySelector?.('theme-drawer#cart-drawer') || null;

    const component = drawer?.matches?.('cart-drawer-component')
      ? drawer
      : drawer?.querySelector?.('cart-drawer-component') || scope.querySelector?.('cart-drawer-component') || null;

    const dialog = drawer?.matches?.('dialog')
      ? drawer
      : drawer?.querySelector?.('dialog') || themeDrawer?.querySelector?.('dialog') || scope.querySelector?.('#CartDrawer dialog, dialog#CartDrawer') || null;

    return { drawer, themeDrawer, component, dialog };
  };

  const replaceCartDrawerContent = (currentSection, incomingSection) => {
    const current = getCartDrawerElements(currentSection || document);
    const incoming = getCartDrawerElements(incomingSection);

    if (!current.drawer || !incoming.drawer) return false;

    const currentInner = current.drawer.querySelector?.('.cart-drawer__inner');
    const incomingInner = incoming.drawer.querySelector?.('.cart-drawer__inner');

    if (currentInner && incomingInner) {
      currentInner.replaceWith(incomingInner.cloneNode(true));
      return true;
    }

    const currentComponent = current.component;
    const incomingComponent = incoming.component;

    if (currentComponent && incomingComponent) {
      currentComponent.replaceWith(incomingComponent.cloneNode(true));
      return true;
    }

    if (current.dialog && incoming.dialog) {
      current.dialog.replaceChildren(...Array.from(incoming.dialog.childNodes, (node) => node.cloneNode(true)));
      return true;
    }

    current.drawer.replaceChildren(...Array.from(incoming.drawer.childNodes, (node) => node.cloneNode(true)));
    return true;
  };

  const replaceRenderedSections = (sections = {}) => {
    const parser = new DOMParser();
    let cartDrawerUpdated = false;

    Object.entries(sections).forEach(([sectionId, html]) => {
      if (!html) return;

      const current = document.getElementById(`shopify-section-${sectionId}`);
      if (!current) return;

      const parsed = parser.parseFromString(html, 'text/html');
      const fullReplacement = parsed.getElementById(`shopify-section-${sectionId}`);
      const incomingSection = fullReplacement || parsed.body;
      const isCartDrawerSection =
        sectionId.includes('cart-drawer') ||
        Boolean(current.querySelector('#cart-drawer, theme-drawer#cart-drawer, cart-drawer-component'));

      if (isCartDrawerSection && replaceCartDrawerContent(current, incomingSection)) {
        cartDrawerUpdated = true;
        return;
      }

      if (fullReplacement) {
        current.replaceChildren(...Array.from(fullReplacement.childNodes, (node) => node.cloneNode(true)));
      } else {
        current.innerHTML = html;
      }
    });

    return cartDrawerUpdated;
  };

  const updateCartBubble = (itemCount) => {
    const count = Number(itemCount || 0);
    const bubbles = document.querySelectorAll('.cart-bubble, [ref="cartBubble"]');

    bubbles.forEach((bubble) => {
      bubble.hidden = false;
      bubble.removeAttribute('aria-hidden');
      bubble.style.removeProperty('visibility');
      bubble.style.removeProperty('display');
      bubble.classList.toggle('is-empty', count === 0);
      bubble.classList.toggle('is-visible', count > 0);

      const countElements = bubble.matches('.cart-bubble__text-count, #cart-bubble-text')
        ? [bubble]
        : bubble.querySelectorAll('.cart-bubble__text-count, #cart-bubble-text, [data-cart-count]');

      countElements.forEach((element) => {
        element.textContent = String(count);
      });
    });

    document.querySelectorAll('.cart-bubble__text-count, #cart-bubble-text, [data-cart-count]').forEach((element) => {
      element.textContent = String(count);
    });
  };

  const fetchCart = async () => {
    const response = await fetch(`${getShopifyRoot()}cart.js`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error('Korpa se nije mogla osvježiti.');
    return response.json();
  };

  const waitForCustomElement = async (name, timeout = 1800) => {
    if (!window.customElements || customElements.get(name)) return;

    await Promise.race([
      customElements.whenDefined(name),
      new Promise((resolve) => window.setTimeout(resolve, timeout)),
    ]);
  };

  const isDrawerOpen = (elements) => {
    if (elements.themeDrawer?.hasAttribute('open')) return true;
    if (elements.dialog?.open) return true;
    if (elements.drawer?.hasAttribute?.('open')) return true;
    return false;
  };

  const openExistingCartDrawer = async () => {
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    let elements = getCartDrawerElements(document);

    if (elements.themeDrawer) {
      await waitForCustomElement('theme-drawer');
      elements = getCartDrawerElements(document);

      if (typeof elements.themeDrawer?.open === 'function') {
        try {
          elements.themeDrawer.open();
          await new Promise((resolve) => requestAnimationFrame(resolve));
          if (isDrawerOpen(elements)) return true;
        } catch (error) {
          console.warn('[Dar Prirode] Horizon theme-drawer nije otvoren direktnim pozivom.', error);
        }
      }
    }

    if (elements.component && typeof elements.component.open === 'function') {
      try {
        elements.component.open();
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (isDrawerOpen(getCartDrawerElements(document))) return true;
      } catch (error) {
        console.warn('[Dar Prirode] Cart drawer component nije otvoren direktnim pozivom.', error);
      }
    }

    const legacyDrawer = document.querySelector('cart-drawer');
    if (legacyDrawer && typeof legacyDrawer.open === 'function') {
      try {
        legacyDrawer.open();
        return true;
      } catch (error) {
        console.warn('[Dar Prirode] Legacy cart drawer nije otvoren.', error);
      }
    }

    elements = getCartDrawerElements(document);
    if (elements.dialog && !elements.dialog.open) {
      try {
        if (window.matchMedia('(max-width: 989px)').matches && typeof elements.dialog.showModal === 'function') {
          elements.dialog.showModal();
        } else if (typeof elements.dialog.show === 'function') {
          elements.dialog.show();
        } else {
          elements.dialog.setAttribute('open', '');
        }

        elements.themeDrawer?.setAttribute('open', '');
        if (elements.drawer && elements.drawer !== elements.themeDrawer) elements.drawer.setAttribute('open', '');
        return true;
      } catch (error) {
        console.warn('[Dar Prirode] Dialog fallback nije otvorio cart drawer.', error);
      }
    }

    const openCartAction = window.Shopify?.actions?.openCart;
    if (typeof openCartAction === 'function' && typeof openCartAction.isDefault === 'function' && !openCartAction.isDefault()) {
      try {
        await openCartAction();
        if (isDrawerOpen(getCartDrawerElements(document))) return true;
      } catch (error) {
        console.warn('[Dar Prirode] Shopify openCart action nije otvorio korpu.', error);
      }
    }

    const trigger = document.querySelector(
      '[aria-controls="cart-drawer"], [data-cart-drawer-open], button[name="cart"], header-actions button[aria-label*="cart" i], header-actions button[aria-label*="korpa" i]'
    );

    if (trigger instanceof HTMLElement) {
      trigger.click();
      await new Promise((resolve) => requestAnimationFrame(resolve));
      return isDrawerOpen(getCartDrawerElements(document));
    }

    return false;
  };

  const addItemsToCart = async (items, options = {}) => {
    const normalizedItems = (Array.isArray(items) ? items : [])
      .map((item) => ({
        id: Number(item.id),
        quantity: Math.max(1, Number(item.quantity || 1)),
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0);

    if (!normalizedItems.length) {
      throw new Error('Nije odabrana dostupna varijanta.');
    }

    const sectionIds = getRenderableSectionIds();
    const payload = { items: normalizedItems };

    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    const response = await fetch(`${getShopifyRoot()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = data?.description || data?.message || 'Proizvod nije mogao biti dodat u korpu.';
      throw new Error(message);
    }

    const cartDrawerUpdated = data.sections ? replaceRenderedSections(data.sections) : false;

    let cart = null;
    try {
      cart = await fetchCart();
      updateCartBubble(cart.item_count);
    } catch (error) {
      console.warn('[Dar Prirode] Proizvod je dodat, ali broj artikala nije osvježen.', error);
    }

    dispatchCartEvents({ source: 'dar-prirode-product-page', cart, response: data });

    let drawerOpened = false;
    if (options.openDrawer !== false) {
      drawerOpened = await openExistingCartDrawer();
      if (!drawerOpened) {
        console.warn('[Dar Prirode] Proizvod je dodat, ali cart drawer nije pronađen ili nije podešen kao tip korpe.');
      }
    }

    return { data, cart, cartDrawerUpdated, drawerOpened };
  };

  window.DarPrirodeCart = {
    ...(window.DarPrirodeCart || {}),
    addItems: addItemsToCart,
    refresh: async () => {
      const cart = await fetchCart();
      updateCartBubble(cart.item_count);
      dispatchCartEvents({ source: 'dar-prirode-manual-refresh', cart });
      return cart;
    },
  };

  const createFocusTrap = (dialog) => {
    const getFocusable = () => Array.from(dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.offsetParent !== null);

    const onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener('keydown', onKeydown);
    return () => dialog.removeEventListener('keydown', onKeydown);
  };

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-dp-gallery]');
    if (!gallery) return;

    const track = gallery.querySelector('[data-dp-gallery-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-dp-gallery-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-dp-gallery-thumb]'));
    const currentLabel = gallery.querySelector('[data-dp-gallery-current]');
    const zoomButton = gallery.querySelector('[data-dp-gallery-zoom]');
    const modal = gallery.querySelector('[data-dp-gallery-modal]');
    const modalContent = gallery.querySelector('[data-dp-gallery-modal-content]');
    const modalClose = gallery.querySelector('[data-dp-gallery-close]');
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let scrollFrame = null;
    let releaseTrap = null;
    let returnFocus = null;

    const setActive = (index, shouldScroll = false) => {
      const nextIndex = Math.max(0, Math.min(index, slides.length - 1));
      activeIndex = nextIndex;

      slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === nextIndex));
      thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === nextIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', String(isActive));
      });

      if (currentLabel) currentLabel.textContent = String(nextIndex + 1).padStart(2, '0');

      if (shouldScroll && slides[nextIndex]) {
        slides[nextIndex].scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      }
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => setActive(Number(thumb.dataset.index || 0), true));
    });

    if (track && slides.length > 1) {
      track.addEventListener('scroll', () => {
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          const trackRect = track.getBoundingClientRect();
          const targetCenter = trackRect.left + trackRect.width / 2;
          let closestIndex = 0;
          let closestDistance = Infinity;

          slides.forEach((slide, index) => {
            const rect = slide.getBoundingClientRect();
            const distance = Math.abs(rect.left + rect.width / 2 - targetCenter);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = index;
            }
          });

          setActive(closestIndex, false);
        });
      }, { passive: true });

      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          setActive(activeIndex + 1, true);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          setActive(activeIndex - 1, true);
        }
      });
    }

    const closeModal = () => {
      if (!modal?.open) return;
      modal.close();
    };

    zoomButton?.addEventListener('click', () => {
      if (!modal || !modalContent || !slides[activeIndex]) return;
      returnFocus = document.activeElement;
      modalContent.replaceChildren();

      const clone = slides[activeIndex].cloneNode(true);
      clone.removeAttribute('data-dp-gallery-slide');
      clone.className = 'dp-gallery-modal__media';
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      modalContent.appendChild(clone);

      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');

      releaseTrap = createFocusTrap(modal);
      modalClose?.focus();
    });

    modalClose?.addEventListener('click', closeModal);
    modal?.addEventListener('click', (event) => {
      if (event.target === modal) closeModal();
    });
    modal?.addEventListener('close', () => {
      releaseTrap?.();
      releaseTrap = null;
      modalContent?.replaceChildren();
      if (returnFocus instanceof HTMLElement) returnFocus.focus();
    });

    gallery.dpSetMediaById = (mediaId) => {
      if (!mediaId) return;
      const index = slides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(mediaId));
      if (index >= 0) setActive(index, true);
    };

    setActive(activeIndex, false);
  };

  const initQuantity = (root) => {
    const input = root.querySelector('[data-dp-quantity-input]');
    const minus = root.querySelector('[data-dp-quantity-minus]');
    const plus = root.querySelector('[data-dp-quantity-plus]');
    if (!input) return;

    const normalize = () => {
      const minimum = Math.max(1, Number(input.min || 1));
      input.value = String(Math.max(minimum, Number(input.value || minimum)));
    };

    minus?.addEventListener('click', () => {
      input.value = String(Math.max(Number(input.min || 1), Number(input.value || 1) - 1));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    plus?.addEventListener('click', () => {
      input.value = String(Number(input.value || 1) + 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    input.addEventListener('change', normalize);
    input.addEventListener('blur', normalize);
  };

  const initVariantController = (root) => {
    const variants = parseJSON(root.querySelector('[data-dp-variants-json]'), []);
    if (!Array.isArray(variants) || !variants.length) return null;

    const idInput = root.querySelector('[data-dp-variant-id]');
    const price = root.querySelector('[data-dp-price]');
    const comparePrice = root.querySelector('[data-dp-compare-price]');
    const discount = root.querySelector('[data-dp-discount]');
    const availability = root.querySelector('[data-dp-availability]');
    const availabilityLabel = root.querySelector('[data-dp-availability-label]');
    const addButton = root.querySelector('[data-dp-add-button]');
    const addLabel = root.querySelector('[data-dp-add-label]');
    const buttonPrice = root.querySelector('[data-dp-button-price]');
    const stickyPrice = root.querySelector('[data-dp-sticky-price]');
    const stickyButton = root.querySelector('[data-dp-sticky-add]');
    const stickyLabel = root.querySelector('[data-dp-sticky-label]');
    const gallery = root.querySelector('[data-dp-gallery]');
    const optionInputs = Array.from(root.querySelectorAll('[data-dp-option-position]'))
      .filter((element) => element.matches('input[type="radio"]'));

    let selectedVariant = variants.find((variant) => Number(variant.id) === Number(idInput?.value)) || variants[0];

    const getSelectedOptions = () => {
      const groups = Array.from(root.querySelectorAll('[data-dp-option-group]'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));

      return groups.map((group) => group.querySelector('input[type="radio"]:checked')?.value || '');
    };

    const updateSelectedLabels = () => {
      root.querySelectorAll('[data-dp-option-group]').forEach((group) => {
        const label = group.querySelector('[data-dp-selected-option]');
        const checked = group.querySelector('input[type="radio"]:checked');
        if (label && checked) label.textContent = checked.value;
      });
    };

    const updateUI = (variant, updateUrl = true) => {
      if (!variant) return;
      selectedVariant = variant;
      const variantPrice = formatMoney(variant.price);
      const hasCompare = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
      const discountPercent = hasCompare
        ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
        : 0;

      if (idInput) idInput.value = String(variant.id);
      if (price) price.textContent = variantPrice;
      if (buttonPrice) buttonPrice.textContent = variantPrice;
      if (stickyPrice) stickyPrice.textContent = variantPrice;

      if (comparePrice) {
        comparePrice.textContent = hasCompare ? formatMoney(variant.compare_at_price) : '';
        comparePrice.classList.toggle('is-hidden', !hasCompare);
      }

      if (discount) {
        discount.textContent = discountPercent > 0 ? `-${discountPercent}%` : '';
        discount.classList.toggle('is-hidden', discountPercent <= 0);
      }

      if (availability) availability.classList.toggle('is-sold-out', !variant.available);
      if (availabilityLabel) availabilityLabel.textContent = variant.available ? 'Dostupno za narudžbu' : 'Trenutno nije dostupno';

      if (addButton) addButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? (addButton?.dataset.addText || 'Dodaj u korpu') : 'Nije dostupno';
      if (stickyButton) stickyButton.disabled = !variant.available;
      if (stickyLabel) stickyLabel.textContent = variant.available ? 'Dodaj' : 'Nedostupno';

      if (variant.featured_media_id && gallery?.dpSetMediaById) {
        gallery.dpSetMediaById(variant.featured_media_id);
      }

      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({ variantId: variant.id }, '', url.toString());
      }

      root.dispatchEvent(new CustomEvent('dp:variant:change', {
        bubbles: true,
        detail: { variant },
      }));
    };

    const selectFromOptions = () => {
      const options = getSelectedOptions();
      const match = variants.find((variant) =>
        variant.options.length === options.length && variant.options.every((value, index) => value === options[index])
      );

      if (match) {
        updateSelectedLabels();
        updateUI(match, true);
      }
    };

    optionInputs.forEach((input) => input.addEventListener('change', selectFromOptions));
    updateSelectedLabels();
    updateUI(selectedVariant, false);

    return {
      getSelectedVariant: () => selectedVariant,
      updateUI,
    };
  };

  const initProductForm = (root, variantController) => {
    const form = root.querySelector('[data-dp-product-form]');
    const button = root.querySelector('[data-dp-add-button]');
    const stickyButton = root.querySelector('[data-dp-sticky-add]');
    const quantityInput = root.querySelector('[data-dp-quantity-input]');
    const message = root.querySelector('[data-dp-form-message]');
    if (!form || !button) return;

    const showMessage = (text, isError = false) => {
      if (!message) return;
      message.textContent = text || '';
      message.classList.toggle('is-error', isError);
    };

    const submit = async () => {
      const variant = variantController?.getSelectedVariant();
      const variantId = Number(variant?.id || form.querySelector('[name="id"]')?.value);
      const quantity = Math.max(1, Number(quantityInput?.value || 1));

      if (!variantId || variant?.available === false) {
        showMessage('Odabrana varijanta trenutno nije dostupna.', true);
        return;
      }

      button.classList.add('is-loading');
      button.disabled = true;
      if (stickyButton) stickyButton.disabled = true;
      showMessage('Dodajemo proizvod u korpu…');

      try {
        await addItemsToCart([{ id: variantId, quantity }], { openDrawer: true });
        showMessage('Proizvod je dodat u korpu.');
      } catch (error) {
        showMessage(error.message || 'Proizvod nije mogao biti dodat u korpu.', true);
      } finally {
        button.classList.remove('is-loading');
        button.disabled = variant?.available === false;
        if (stickyButton) stickyButton.disabled = variant?.available === false;
      }
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submit();
    });

    stickyButton?.addEventListener('click', submit);
  };

  const initStickyCart = (root) => {
    const sticky = root.querySelector('[data-dp-sticky-cart]');
    const mainButton = root.querySelector('[data-dp-main-buy-button]');
    if (!sticky || !mainButton || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      const passedAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', passedAboveViewport);
      sticky.setAttribute('aria-hidden', String(!passedAboveViewport));
    }, { threshold: 0.02 });

    observer.observe(mainButton);
  };

  const initRoot = (root) => {
    if (!(root instanceof HTMLElement) || initializedRoots.has(root)) return;
    initializedRoots.add(root);

    initGallery(root);
    initQuantity(root);
    const variantController = initVariantController(root);
    initProductForm(root, variantController);
    initStickyCart(root);
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  const boot = () => initAll(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  window.DarPrirodeProductPage = {
    loaded: true,
    init: initAll,
    formatMoney,
  };
})();
